import { formatMicro, type Micro, microFromString } from "../exact-numbers.js";
import { EMPTY_TROOPS, travelDeadline } from "../logistics-math.js";
import type { TroopComposition } from "../logistics-types.js";
import { UNIT_IDS } from "../logistics-types.js";
import { effectMultiplier, effectsForResearch } from "../research-effects.js";
import type { SeatObservation } from "../types.js";
import type { MilitaryDraft } from "./app-source.js";
import { seatName, troopsText } from "./border-situation.js";
import { type AppNode, appAction, gameAction, text } from "./view-nodes.js";

export function transferShare(troops: TroopComposition, percent: number): TroopComposition {
  return Object.fromEntries(
    UNIT_IDS.map((unit) => [
      unit,
      formatMicro(((microFromString(troops[unit]) * BigInt(percent)) / 100n) as Micro),
    ]),
  ) as TroopComposition;
}

export function manualTransfers(o: SeatObservation, id: string, draft: MilitaryDraft): AppNode {
  const town = seatName(o, id);
  const percent = draft.transferPercent ?? 50;
  const deployed = transferShare(o.self.logistics.borders[id]?.troops ?? EMPTY_TROOPS, percent);
  const legSeconds =
    travelDeadline(
      0,
      effectMultiplier(effectsForResearch(o.self.research), "military-travel-time"),
      250,
    ) / 1000;
  return {
    kind: "infobox",
    id: `manual-${id}`,
    title: `Move troops · ${town}`,
    initiallyOpen: false,
    content: [
      {
        kind: "row",
        id: `transfer-amounts-${id}`,
        children: [25, 50, 100].map((value) =>
          appAction(`transfer-amount-${id}-${value}`, value === 100 ? "All" : `${value}%`, {
            kind: "set-military-draft",
            borderId: id,
            change: { transferPercent: value },
          }),
        ),
      },
      text(`manual-note-${id}`, `Moving ${percent}%: ${troopsText(deployed)}`),
      text(
        `manual-route-${id}`,
        `Other border: via home, about ${legSeconds * 2} game seconds. Withdraw to home: ${legSeconds} game seconds. Troops do not fight while traveling.`,
      ),
      {
        kind: "range-input",
        id: `transfer-share-${id}`,
        label: "Share of deployed troops",
        value: percent,
        min: 0,
        max: 100,
        step: 5,
        previewIntent: (value) => ({
          kind: "set-military-draft",
          borderId: id,
          change: { transferPercent: value },
        }),
        intent: (value) => ({
          kind: "set-military-draft",
          borderId: id,
          change: { transferPercent: value },
        }),
      },
      ...Object.keys(o.self.logistics.borders)
        .filter((destination) => destination !== id)
        .map((destination) =>
          gameAction(
            `transfer-${id}-${destination}`,
            `Move from ${town} to ${seatName(o, destination)}`,
            { kind: "redeploy-troops", origin: id, destination, units: deployed },
          ),
        ),
      gameAction(`return-${id}`, `Withdraw from ${town} to reserves`, {
        kind: "return-troops",
        origin: id,
        units: deployed,
      }),
    ],
  };
}

export function transitView(o: SeatObservation): AppNode {
  const orders = o.self.logistics.transits;
  return {
    kind: "infobox",
    id: "transits",
    title: `Troops in transit — ${orders.length}`,
    initiallyOpen: true,
    content: orders.length
      ? orders.flatMap((order) => [
          text(
            `${order.id}-transit`,
            `${troopsText(order.units)} → ${seatName(o, order.destination)}${order.finalDestination ? `, then ${seatName(o, order.finalDestination)}` : ""}; ${Math.max(0, Math.ceil((order.completesAtMs - o.gameTimeMs) / 1_000))} game seconds remain.${order.returnAfterArrival ? " Return to reserves queued after arrival." : ""}`,
          ),
          gameAction(`cancel-${order.id}`, "Cancel transit", {
            kind: "cancel-transit",
            orderId: order.id,
          }),
        ])
      : [text("transits-empty", "No troops are in transit.")],
  };
}

export function incomingTroops(o: SeatObservation, id: string): string {
  const orders = o.self.logistics.transits.filter(
    (order) =>
      !order.returnAfterArrival && (order.destination === id || order.finalDestination === id),
  );
  return orders.length
    ? orders
        .map(
          (order) =>
            `${troopsText(order.units)}; ${Math.max(0, Math.ceil((order.completesAtMs - o.gameTimeMs) / 1_000))} game seconds to ${order.finalDestination ? "reserves, then another travel leg" : "arrival"}`,
        )
        .join(" / ")
    : "None traveling here.";
}
